import React, { useMemo, useState } from 'react';

import styled from '@emotion/styled';

import { Box } from '@clutter/clean';

import { Nav } from '@shared/components/bootstrap';

import { Panel } from '@admin/components/helpers/panel';
import { InventorySummary } from '@admin/components/orders/inventory_summary';
import { Spinner } from '@admin/components/spinner';
import { client } from '@admin/libraries/apollo';
import {
  Estimation__AlgorithmName,
  OrderSubtypeEnum,
  OrderTypeEnum,
  useEstimationAlgorithmsQuery,
  useEstimationAlgorithmForOrderQuery,
  useOrderShowQuery,
} from '@admin/schema';

import { OrderPanel } from '../order/panel';
import { Uploads as EstimationUploads } from '../order/uploads';
import { Table as ItemsTable } from './items/table';

const InventoryContainer = styled(Box.FlexItem)<{ $compact?: boolean }>`
  min-width: ${({ $compact }) => ($compact ? 0 : '700px')};
  flex: ${({ $compact }) => ($compact ? '1 1 55%' : 'initial')};
`;

const UploadsContainer = styled(Box.FlexItem)<{ $compact?: boolean }>`
  min-width: ${({ $compact }) => ($compact ? '200px' : '400px')};
  width: ${({ $compact }) => ($compact ? '45%' : '40%')};
  margin: 10px ${({ $compact }) => ($compact ? '10px' : '30px')};
  text-align: center;
`;

const DEFAULT_COLLAPSED = false;

type TabKey = 'customer' | 'agent' | 'actual';

export const EstimationFlowInsightPanel: React.FC<{
  orderID: string;
  moving: boolean;
  /** Relaxes min-widths when the panel shares a row with Location Details. */
  compactLayout?: boolean;
}> = ({ orderID, moving, compactLayout = false }) => {
  const [collapsed, setCollapsed] = useState<boolean>(DEFAULT_COLLAPSED);
  const [activeTab, setActiveTab] = useState<TabKey>('customer');
  const [orderAlgorithmID, setOrderAlgorithmID] = useState<string>('');

  const {
    data: orderShow,
    loading: orderShowLoading,
    error: orderShowError,
  } = useOrderShowQuery({
    client,
    variables: { id: orderID },
    skip: !orderID,
  });
  const {
    data,
    loading: estimationAlgorithmsLoading,
    error: estimationAlgorithmsError,
    refetch: refetchEstimationAlgorithms,
  } = useEstimationAlgorithmsQuery({ client });
  useEstimationAlgorithmForOrderQuery({
    client,
    variables: { orderID },
    onCompleted: (estimationAlgorithm) => {
      setOrderAlgorithmID(estimationAlgorithm.estimationAlgorithmForOrder?.id ?? '');
    },
  });

  const estimationAlgorithmsReady = !estimationAlgorithmsLoading && !estimationAlgorithmsError;

  const { agentEstimationID, onsiteItemizationID, v1AlgorithmID, v2AlgorithmID, virtualWalkthroughID } = useMemo(() => {
    const empty = {
      agentEstimationID: undefined as string | undefined,
      onsiteItemizationID: undefined as string | undefined,
      v1AlgorithmID: undefined as string | undefined,
      v2AlgorithmID: undefined as string | undefined,
      virtualWalkthroughID: undefined as string | undefined,
    };
    if (!estimationAlgorithmsReady || !data?.estimationAlgorithms?.length) {
      return empty;
    }
    const algorithms = data.estimationAlgorithms;
    const result = { ...empty };
    for (const algorithm of algorithms) {
      switch (algorithm.name) {
        case Estimation__AlgorithmName.AgentEstimation:
          result.agentEstimationID = algorithm.id;
          break;
        case Estimation__AlgorithmName.OnsiteItemization:
          result.onsiteItemizationID = algorithm.id;
          break;
        case Estimation__AlgorithmName.V1:
          result.v1AlgorithmID = algorithm.id;
          break;
        case Estimation__AlgorithmName.V2:
          result.v2AlgorithmID = algorithm.id;
          break;
        case Estimation__AlgorithmName.VirtualWalkthrough:
          result.virtualWalkthroughID = algorithm.id;
          break;
        default:
          break;
      }
    }
    return result;
  }, [data?.estimationAlgorithms, estimationAlgorithmsReady]);

  // Pickups store customer/agent lines under VW/V2 + agent_estimation; onsite_itemization is for moves.
  const actualAlgorithmID = moving ? onsiteItemizationID : agentEstimationID;
  const actualItemsAgentOnly = !moving && Boolean(agentEstimationID);

  // Subsequent pickups use Item Selection on the customer tab, not the Virtual Walkthrough OrderPanel.
  // We still need a stable "customer" algorithm id for agent prefill (prefillSourceAlgorithmID) and
  // fallbacks; V2 matches the itemization path for sub pickups, with orderAlgorithmID / V1 as backup.
  const orderShowReady =
    !orderShowLoading &&
    !orderShowError &&
    Boolean(orderShow?.order) &&
    (orderShow?.order?.type !== OrderTypeEnum.Pickup || orderShow.order.subtype != null);

  const isSubPickup =
    orderShowReady &&
    orderShow!.order!.type === OrderTypeEnum.Pickup &&
    orderShow!.order!.subtype === OrderSubtypeEnum.Subsequent;

  const customerAlgorithmID = orderShowReady
    ? (isSubPickup ? v2AlgorithmID : virtualWalkthroughID) || orderAlgorithmID || v1AlgorithmID || ''
    : '';

  const customerIsVirtualWalkthrough = Boolean(virtualWalkthroughID && customerAlgorithmID === virtualWalkthroughID);

  return (
    <Panel>
      <Panel.Header>
        <Panel.Collapse collapsed={collapsed} onChange={setCollapsed} />
        <Panel.Title>Order Estimates</Panel.Title>
      </Panel.Header>
      <Panel.Body>
        {!collapsed && (
          <Box.Flex flexDirection="row" flexWrap="wrap" alignItems="flex-start">
            <InventoryContainer order={[2, 2, 1]} $compact={compactLayout}>
              <Nav format={Nav.Format.Tabs} role="tablist">
                <Nav.Item className={activeTab === 'customer' ? 'active' : ''}>
                  <Nav.Link
                    id="estimation-flow-tab-customer"
                    role="tab"
                    aria-controls="estimation-flow-panel-customer"
                    aria-selected={activeTab === 'customer'}
                    onClick={() => setActiveTab('customer')}
                  >
                    Customer Input
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item className={activeTab === 'agent' ? 'active' : ''}>
                  <Nav.Link
                    id="estimation-flow-tab-agent"
                    role="tab"
                    aria-controls="estimation-flow-panel-agent"
                    aria-selected={activeTab === 'agent'}
                    onClick={() => setActiveTab('agent')}
                  >
                    Agent Estimate
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item className={activeTab === 'actual' ? 'active' : ''}>
                  <Nav.Link
                    id="estimation-flow-tab-actual"
                    role="tab"
                    aria-controls="estimation-flow-panel-actual"
                    aria-selected={activeTab === 'actual'}
                    onClick={() => setActiveTab('actual')}
                  >
                    Field Pickup (Actual)
                  </Nav.Link>
                </Nav.Item>
              </Nav>
              <hr />
              {activeTab === 'customer' && (
                <div id="estimation-flow-panel-customer" role="tabpanel" aria-labelledby="estimation-flow-tab-customer">
                  {orderShowError && (
                    <span role="alert">
                      Failed to load order
                      {orderShowError.message ? `: ${orderShowError.message}` : ''}
                    </span>
                  )}
                  {orderShowLoading && <Spinner />}
                  {!orderShowLoading && !orderShowError && !isSubPickup && estimationAlgorithmsLoading && <Spinner />}
                  {!orderShowLoading && !orderShowError && !isSubPickup && estimationAlgorithmsError && (
                    <span role="alert">
                      Failed to load estimation algorithms
                      {estimationAlgorithmsError.message ? `: ${estimationAlgorithmsError.message}` : ''}.{' '}
                      <button type="button" className="btn btn-link" onClick={() => void refetchEstimationAlgorithms()}>
                        Retry
                      </button>
                    </span>
                  )}
                  {!orderShowLoading && !orderShowError && isSubPickup && (
                    <>
                      <h3 className="text-thin">Item Selection</h3>
                      <InventorySummary orderID={orderID} />
                    </>
                  )}
                  {!orderShowLoading &&
                    !orderShowError &&
                    !isSubPickup &&
                    !estimationAlgorithmsLoading &&
                    !estimationAlgorithmsError &&
                    orderShowReady &&
                    !customerAlgorithmID && (
                      <p className="text-muted" role="status">
                        Customer estimation is not available for this order.
                      </p>
                    )}
                  {!orderShowLoading &&
                    !orderShowError &&
                    !isSubPickup &&
                    !estimationAlgorithmsLoading &&
                    !estimationAlgorithmsError &&
                    customerAlgorithmID && (
                      <OrderPanel
                        columnMode="customer"
                        orderID={orderID}
                        algorithmID={customerAlgorithmID}
                        isVirtualWalkthrough={customerIsVirtualWalkthrough}
                      />
                    )}
                </div>
              )}
              {activeTab === 'agent' && (
                <div id="estimation-flow-panel-agent" role="tabpanel" aria-labelledby="estimation-flow-tab-agent">
                  {estimationAlgorithmsError && (
                    <span role="alert">
                      Failed to load estimation algorithms
                      {estimationAlgorithmsError.message ? `: ${estimationAlgorithmsError.message}` : ''}.{' '}
                      <button type="button" className="btn btn-link" onClick={() => void refetchEstimationAlgorithms()}>
                        Retry
                      </button>
                    </span>
                  )}
                  {!estimationAlgorithmsError && estimationAlgorithmsLoading && <Spinner />}
                  {!estimationAlgorithmsError && !estimationAlgorithmsLoading && !agentEstimationID && (
                    <>
                      <h3 className="text-thin">Item Selection</h3>
                      <p className="text-muted" role="status">
                        Agent estimation is not available for this order.
                      </p>
                    </>
                  )}
                  {!estimationAlgorithmsError && !estimationAlgorithmsLoading && agentEstimationID && (
                    <OrderPanel
                      columnMode="agent"
                      orderID={orderID}
                      algorithmID={agentEstimationID}
                      isVirtualWalkthrough={false}
                      prefillSourceAlgorithmID={orderShowReady && customerAlgorithmID ? customerAlgorithmID : undefined}
                    />
                  )}
                </div>
              )}
              {activeTab === 'actual' && (
                <div id="estimation-flow-panel-actual" role="tabpanel" aria-labelledby="estimation-flow-tab-actual">
                  {estimationAlgorithmsError && (
                    <span role="alert">
                      Failed to load estimation algorithms
                      {estimationAlgorithmsError.message ? `: ${estimationAlgorithmsError.message}` : ''}.{' '}
                      <button type="button" className="btn btn-link" onClick={() => void refetchEstimationAlgorithms()}>
                        Retry
                      </button>
                    </span>
                  )}
                  {!estimationAlgorithmsError && estimationAlgorithmsLoading && <Spinner />}
                  {!estimationAlgorithmsError && !estimationAlgorithmsLoading && !actualAlgorithmID && (
                    <>
                      <h3 className="text-thin">Item Selection</h3>
                      <p className="text-muted" role="status">
                        {moving
                          ? 'Onsite itemization is not available for this order.'
                          : 'Agent estimation is not available for this order.'}
                      </p>
                    </>
                  )}
                  {!estimationAlgorithmsError && !estimationAlgorithmsLoading && actualAlgorithmID && (
                    <ItemsTable
                      orderID={orderID}
                      algorithmID={actualAlgorithmID}
                      disassemblyFlow={moving}
                      agentOnly={actualItemsAgentOnly}
                    />
                  )}
                </div>
              )}
            </InventoryContainer>
            <UploadsContainer order={[1, 1, 2]} $compact={compactLayout}>
              <h3 className="text-thin">Customer Uploads</h3>
              <EstimationUploads orderID={orderID} />
            </UploadsContainer>
          </Box.Flex>
        )}
      </Panel.Body>
    </Panel>
  );
};
